import { useState, useEffect } from 'react';
import { apiFetch } from '../api';

import { CategoryOptions } from '../categories';
const MATCH_FIELDS = [
  { value: 'description', label: 'Description' },
  { value: 'accountName', label: 'Account Name' },
  { value: 'memo', label: 'Memo' },
];
const MATCH_TYPES = [
  { value: 'contains', label: 'Contains' },
  { value: 'startsWith', label: 'Starts With' },
  { value: 'endsWith', label: 'Ends With' },
  { value: 'exact', label: 'Exact Match' },
];

const SPENDING_TYPES = [
  { value: 'bills', label: 'Normal Bills' },
  { value: 'controllable', label: 'Controllable' },
  { value: 'unplanned', label: 'Unplanned' },
];

function CategoryRulesPanel({ refreshKey, onRulesApplied }) {
  const [rules, setRules] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [applying, setApplying] = useState(false);
  const [applyResult, setApplyResult] = useState(null);
  const [tab, setTab] = useState('rules'); // 'rules' or 'suggestions'
  const [ruleFilter, setRuleFilter] = useState('');
  const [form, setForm] = useState({ pattern: '', matchField: 'description', matchType: 'contains', category: '', spendingType: '', priority: 100, amountMin: '', amountMax: '' });

  useEffect(() => { fetchData(); }, [refreshKey]);

  async function fetchData() {
    try {
      const [rulesRes, suggestionsRes] = await Promise.all([
        apiFetch('/api/category-rules'),
        apiFetch('/api/category-rules/suggestions'),
      ]);
      if (rulesRes.ok) setRules(await rulesRes.json());
      if (suggestionsRes.ok) setSuggestions(await suggestionsRes.json());
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  async function handleSave() {
    const payload = {
      ...form,
      priority: parseInt(form.priority) || 100,
      amountMin: form.amountMin !== '' ? parseFloat(form.amountMin) : null,
      amountMax: form.amountMax !== '' ? parseFloat(form.amountMax) : null,
    };
    if (editingId) {
      const res = await apiFetch(`/api/category-rules/${editingId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...payload, enabled: true }),
      });
      if (res.ok) {
        const updated = await res.json();
        setRules((prev) => prev.map((r) => (r.id === editingId ? updated : r)));
      }
    } else {
      const res = await apiFetch('/api/category-rules', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        const created = await res.json();
        setRules((prev) => [...prev, created]);
      }
    }
    resetForm();
  }

  async function handleDelete(id) {
    await apiFetch(`/api/category-rules/${id}`, { method: 'DELETE' });
    setRules((prev) => prev.filter((r) => r.id !== id));
  }

  async function handleToggle(rule) {
    const res = await apiFetch(`/api/category-rules/${rule.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...rule, enabled: !rule.enabled }),
    });
    if (res.ok) {
      const updated = await res.json();
      setRules((prev) => prev.map((r) => (r.id === rule.id ? updated : r)));
    }
  }

  async function handleApply() {
    setApplying(true);
    setApplyResult(null);
    try {
      const res = await apiFetch('/api/category-rules/apply', { method: 'POST' });
      if (res.ok) {
        const data = await res.json();
        setApplyResult(`Categorized ${data.categorized} transaction${data.categorized !== 1 ? 's' : ''}`);
        if (data.categorized > 0 && onRulesApplied) onRulesApplied();
        // Refresh suggestions
        const sugRes = await apiFetch('/api/category-rules/suggestions');
        if (sugRes.ok) setSuggestions(await sugRes.json());
      }
    } catch (err) {
      setApplyResult('Failed to apply rules');
    } finally {
      setApplying(false);
      setTimeout(() => setApplyResult(null), 5000);
    }
  }

  async function acceptSuggestion(suggestion) {
    try {
      await apiFetch(`/api/simplefin/transactions/${encodeURIComponent(suggestion.transactionId)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ category: suggestion.suggestedCategory }),
      });
      setSuggestions((prev) => prev.filter((s) => s.transactionId !== suggestion.transactionId));
      if (onRulesApplied) onRulesApplied();
    } catch (err) {
      console.error(err);
    }
  }

  function createRuleFromSuggestion(suggestion) {
    setForm({
      pattern: suggestion.description,
      matchField: 'description',
      matchType: 'contains',
      category: suggestion.suggestedCategory,
      spendingType: '',
      priority: 100,
    });
    setShowAdd(true);
    setEditingId(null);
    setTab('rules');
  }

  function startEdit(rule) {
    setForm({
      pattern: rule.pattern,
      matchField: rule.matchField,
      matchType: rule.matchType,
      category: rule.category,
      spendingType: rule.spendingType || '',
      priority: rule.priority,
      amountMin: rule.amountMin != null ? rule.amountMin : '',
      amountMax: rule.amountMax != null ? rule.amountMax : '',
    });
    setEditingId(rule.id);
    setShowAdd(true);
  }

  function resetForm() {
    setForm({ pattern: '', matchField: 'description', matchType: 'contains', category: '', spendingType: '', priority: 100, amountMin: '', amountMax: '' });
    setShowAdd(false);
    setEditingId(null);
  }

  if (loading) {
    return <section className="card"><h2>Category Rules</h2><p className="loading">Loading...</p></section>;
  }

  return (
    <section className="card cr-section">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <h2 style={{ margin: 0 }}>Category Rules</h2>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="manage-btn" onClick={handleApply} disabled={applying}>
            {applying ? 'Applying...' : 'Apply Rules'}
          </button>
          <button className="manage-btn" onClick={() => { resetForm(); setShowAdd(true); }}>+ Add Rule</button>
        </div>
      </div>

      {applyResult && (
        <div className="sfin-import-result sfin-import-success" style={{ marginBottom: 8 }}>
          <span>{applyResult}</span>
          <button className="modal-close" onClick={() => setApplyResult(null)} style={{ marginLeft: 8, background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', fontSize: 16 }}>&times;</button>
        </div>
      )}

      <div className="wg-filter-tabs" style={{ marginBottom: 12 }}>
        <button className={`wg-filter-tab${tab === 'rules' ? ' active' : ''}`} onClick={() => setTab('rules')}>
          Rules ({rules.length})
        </button>
        <button className={`wg-filter-tab${tab === 'suggestions' ? ' active' : ''}`} onClick={() => setTab('suggestions')}>
          Suggestions ({suggestions.length})
        </button>
      </div>

      {/* Add/Edit Form */}
      {showAdd && (
        <div className="cr-form" style={{ marginBottom: 12, padding: 12, background: '#16171f', borderRadius: 8, border: '1px solid #2a2b35' }}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <span style={{ color: '#71717a', fontSize: 13 }}>If</span>
            <select value={form.matchField} onChange={(e) => setForm({ ...form, matchField: e.target.value })}
              className="sfin-tx-select" style={{ width: 'auto' }}>
              {MATCH_FIELDS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
            </select>
            <select value={form.matchType} onChange={(e) => setForm({ ...form, matchType: e.target.value })}
              className="sfin-tx-select" style={{ width: 'auto' }}>
              {MATCH_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
            <input type="text" value={form.pattern} onChange={(e) => setForm({ ...form, pattern: e.target.value })}
              placeholder="Pattern..." className="sfin-tx-search" style={{ flex: 1, minWidth: 120 }} />
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 8, alignItems: 'center' }}>
            <span style={{ color: '#71717a', fontSize: 13 }}>then set</span>
            <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
              className="sfin-tx-select" style={{ width: 'auto' }}>
              <option value="">Category...</option>
              {CategoryOptions()}
            </select>
            <select value={form.spendingType} onChange={(e) => setForm({ ...form, spendingType: e.target.value })}
              className="sfin-tx-select" style={{ width: 'auto' }}>
              <option value="">Spending type...</option>
              {SPENDING_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
            <span style={{ color: '#71717a', fontSize: 13, marginLeft: 8 }}>Amount:</span>
            <input type="number" value={form.amountMin} onChange={(e) => setForm({ ...form, amountMin: e.target.value })}
              placeholder="Min $" step="0.01" min="0"
              style={{ width: 75, padding: '4px 8px', background: '#1a1b23', color: '#e4e4e7', border: '1px solid #3a3b45', borderRadius: 6, fontSize: 13 }} />
            <span style={{ color: '#71717a', fontSize: 13 }}>–</span>
            <input type="number" value={form.amountMax} onChange={(e) => setForm({ ...form, amountMax: e.target.value })}
              placeholder="Max $" step="0.01" min="0"
              style={{ width: 75, padding: '4px 8px', background: '#1a1b23', color: '#e4e4e7', border: '1px solid #3a3b45', borderRadius: 6, fontSize: 13 }} />
            <span style={{ color: '#71717a', fontSize: 13, marginLeft: 8 }}>Priority:</span>
            <input type="number" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })}
              style={{ width: 60, padding: '4px 8px', background: '#1a1b23', color: '#e4e4e7', border: '1px solid #3a3b45', borderRadius: 6, fontSize: 13 }} />
            <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
              <button className="manage-btn" onClick={handleSave} disabled={!form.pattern || (!form.category && !form.spendingType)}>
                {editingId ? 'Update' : 'Create'}
              </button>
              <button className="manage-btn" onClick={resetForm} style={{ background: '#2a2b35' }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Rules Tab */}
      {tab === 'rules' && (
        <div className="cr-rules-list">
          <input type="text" value={ruleFilter} onChange={(e) => setRuleFilter(e.target.value)}
            placeholder="Filter rules..." className="sfin-tx-search" style={{ marginBottom: 8 }} />
          {rules.length === 0 && <p style={{ color: '#71717a', fontSize: 13 }}>No rules yet. Add one to auto-categorize transactions.</p>}
          {rules.filter((rule) => {
            if (!ruleFilter.trim()) return true;
            const q = ruleFilter.toLowerCase();
            return rule.pattern.toLowerCase().includes(q) || rule.category.toLowerCase().includes(q)
              || (rule.spendingType && rule.spendingType.toLowerCase().includes(q));
          }).map((rule) => (
            <div key={rule.id} className={`cr-rule-row${!rule.enabled ? ' cr-disabled' : ''}`}>
              <div className="cr-rule-check" onClick={() => handleToggle(rule)}>
                <input type="checkbox" checked={rule.enabled} readOnly />
              </div>
              <div className="cr-rule-info">
                <span className="cr-rule-pattern">
                  If <strong>{rule.matchField}</strong> {rule.matchType} "<strong>{rule.pattern}</strong>"
                  {(rule.amountMin != null || rule.amountMax != null) && (
                    <span style={{ color: '#71717a', fontSize: 11, marginLeft: 4 }}>
                      {rule.amountMin != null && rule.amountMax != null
                        ? `($${rule.amountMin}–$${rule.amountMax})`
                        : rule.amountMin != null
                          ? `(≥ $${rule.amountMin})`
                          : `(≤ $${rule.amountMax})`}
                    </span>
                  )}
                </span>
                <span className="cr-rule-arrow"> → </span>
                {rule.category && <span className="sfin-tx-category-badge">{rule.category}</span>}
                {rule.spendingType && <span className={`sfin-tx-spending-badge sfin-st-${rule.spendingType}`}>{SPENDING_TYPES.find(t => t.value === rule.spendingType)?.label || rule.spendingType}</span>}
              </div>
              <div className="cr-rule-actions">
                <button className="wg-action-btn wg-edit-btn" onClick={() => startEdit(rule)} title="Edit">&#x270E;</button>
                <button className="wg-action-btn wg-delete-btn" onClick={() => handleDelete(rule.id)} title="Delete">&times;</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Suggestions Tab */}
      {tab === 'suggestions' && (
        <div className="cr-suggestions-list">
          {suggestions.length === 0 && <p style={{ color: '#71717a', fontSize: 13 }}>No suggestions. Categorize more transactions to build up patterns.</p>}
          {suggestions.map((s) => (
            <div key={s.transactionId} className="cr-suggestion-row">
              <div className="cr-suggestion-info">
                <span className="cr-suggestion-desc">{s.description}</span>
                <span className="cr-suggestion-meta">
                  <span className="sfin-tx-category-badge">{s.suggestedCategory}</span>
                  <span style={{ color: '#71717a', fontSize: 11 }}>{s.confidence}% confident ({s.matchCount} similar)</span>
                </span>
              </div>
              <div className="cr-suggestion-actions">
                <button className="manage-btn" onClick={() => acceptSuggestion(s)} style={{ fontSize: 12, padding: '4px 10px' }}>Accept</button>
                <button className="manage-btn" onClick={() => createRuleFromSuggestion(s)}
                  style={{ fontSize: 12, padding: '4px 10px', background: '#2a2b35' }}>Create Rule</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

export default CategoryRulesPanel;
